/**
 * Parça bitince sıradakine **otomatik** geçiş.
 *
 * ## Neden ayrı kanca?
 *
 * Oynatma ekranı (`WatchScreen`) zaten çok sorumluluk taşıyor: karar, kaynak,
 * altyazı, raporlama, tam ekran, tercihler. "Bittiğinde ne olacak?" kuralı
 * bunlardan biri değil; ayrı bir kancada tutulur:
 *
 *   - kural tek yerde okunur (bölümde otomatik geçiş **yok**),
 *   - `WatchScreen`ın karmaşıklığı artmaz,
 *   - kural tek başına test edilebilir.
 */

import { useEffect, useRef } from 'react';

export interface UseAutoAdvanceOptions {
  /** Oynatma bitti mi? */
  isEnded: boolean;
  /**
   * Oynatılan konum (ms).
   *
   * ## Neden gerekli?
   *
   * Bozuk/sıfır süreli bir akışta `ended` **anında** tetiklenebilir. Bu durumda
   * `isEnded` doğru olsa bile geçiş yapmak, kütüphaneyi saniyeler içinde
   * tarayan bir zincir üretir (müzik kütüphanesinde gerçekten görüldü:
   * tüm parçalar "izlendi" oldu). Gerçek bir bitişte konum > 0'dır; bu eşik
   * yalnızca "hiç oynamadan bitti" durumunu eler.
   */
  positionMs: number;
  /**
   * Sıradaki içeriğin anlamı.
   *
   * `track` = müzik parçası → otomatik geçilir.
   * `episode` = bölüm → **geçilmez**; kullanıcı "sıradaki bölüm" düğmesine
   * basar.
   */
  relation: 'track' | 'episode' | null;
  /** Sıradaki içerik var mı? */
  hasNext: boolean;
  /** Sıradakine geç. */
  onNext: () => void;
}

/** "Hiç oynamadan bitti" sayılan üst sınır. */
const MIN_PLAYED_MS = 1_000;

/**
 * Oynatma bittiğinde sıradaki içeriğe geç (yalnız müzik parçaları).
 */
export function useAutoAdvance({
  isEnded,
  positionMs,
  relation,
  hasNext,
  onNext,
}: UseAutoAdvanceOptions): void {
  const advancedRef = useRef(false);

  useEffect(() => {
    // Oynatma yeniden başladıysa (kullanıcı başa sarar) sıradaki geçiş
    // yeniden mümkündür.
    if (!isEnded) {
      advancedRef.current = false;
      return;
    }
    // Aynı "bitti" olayı için ikinci kez geçiş yapılmaz.
    if (advancedRef.current) return;
    // Bölümlerde otomatik geçiş yok.
    if (relation !== 'track') return;
    if (!hasNext) return;
    /*
     * Hiç oynatılmadan biten akışta geçiş yapılmaz. Bu, "sona at → anında
     * bit → sıradaki" zincirinin son savunma hattıdır; asıl düzeltme
     * `resumePositionMs` (izlenmiş içerik baştan başlar).
     */
    if (positionMs < MIN_PLAYED_MS) return;

    advancedRef.current = true;
    onNext();
  }, [isEnded, positionMs, relation, hasNext, onNext]);
}
